import { useRouter } from 'next/router';
import styled from 'styled-components';
import { VisuallyHidden } from '@spotify-internal/encore-web';
import { GridLayout } from '@spotify-internal/encore-web/content-platform/components/GridLayout';
import { sendEvent } from '@spotify-internal/s4mp-page-analytics';
import { useArtistInfo } from '@hooks/useArtistInfo';
import { usePagination } from '@hooks/usePagination';
import { useRouteUrlHistory } from '@context/RouteUrlHistory';
import { ArtistBreadcrumbs } from '@components/Artist/ArtistBreadcrumbs';
import { ArtistOverviewTabs } from '@components/Artist/ArtistOverviewTabs';
import { EntityHeader } from '@components/Artist/EntityHeader';
import { Metadata } from '@components/Artist/Metadata';
import { Loading } from '@components/shared/Loading';
import { RetryableLoadingError } from '@components/shared/RetryableLoadingError';
import { withAuth } from '@components/shared/withAuth';
import { CommandPaletteContainer } from '@lib/commandPalette';
import { buildArtistPageCommandPalette } from '@lib/commandPalette/helpers';
import { EVENT_ACTION, EVENT_CATEGORY } from '@lib/ga/types';
import { GA_PRODUCT_CATALOG } from '@helpers/constants';

const Container = styled.section`
  display: flex;
  flex-direction: column;
  margin-bottom: 160px;
`;

const ErrorContainer = styled.div`
  height: 700px;
  display: flex;
  justify-content: center;
`;

export const ArtistPage = () => {
  const router = useRouter();
  const { id: artistUri, fromRelease, fromTrack } = router.query;
  const {
    limit,
    page,
    renderPagination: renderArtistReleasesPagination,
  } = usePagination();

  const {
    data: { artistInfo },
    error,
    status,
    mutate,
    isValidating,
    isLoading,
  } = useArtistInfo(
    typeof artistUri === 'string' ? artistUri.split('?')[0] : '',
    page,
    limit,
  );

  const { previousRoute, searchQueryParams } = useRouteUrlHistory();
  const showBreadcrumbs =
    (!!previousRoute &&
      (previousRoute.includes('/release') ||
        previousRoute.includes('/search') ||
        previousRoute.includes('/track'))) ||
    previousRoute === '/catalog';

  const onRetry = () => {
    sendEvent({
      eventCategory: EVENT_CATEGORY.ARTIST_PAGE,
      eventAction: EVENT_ACTION.CLICK,
      eventLabel: 'Retried artist page fetch',
      product: GA_PRODUCT_CATALOG,
    });

    mutate();
  };

  if (isLoading || isValidating) {
    return <Loading data-screen="artist" />;
  }

  if (error || status !== 200 || !artistInfo) {
    return (
      <ErrorContainer>
        <RetryableLoadingError onRetry={onRetry} />
      </ErrorContainer>
    );
  }

  const {
    name,
    uri,
    url,
    albums,
    avatarUri,
    isVerified,
    albumsPaginationContext,
  } = artistInfo;

  return (
    <Container>
      <GridLayout>
        {showBreadcrumbs && (
          <ArtistBreadcrumbs
            searchQueryParams={searchQueryParams}
            fromReleaseId={
              typeof fromRelease === 'string' ? fromRelease : undefined
            }
            fromTrackId={typeof fromTrack === 'string' ? fromTrack : undefined}
          />
        )}
        <VisuallyHidden>
          <h1>Artist Page</h1>
        </VisuallyHidden>
        <EntityHeader
          artistName={name}
          artistUri={uri}
          albumsLength={albumsPaginationContext.totalRows}
          avatarUri={avatarUri}
          isVerified={isVerified}
        />
        <Metadata uri={uri} url={url} />
        <ArtistOverviewTabs
          artistName={name}
          artistUri={uri}
          albums={albums || []}
          paginationProps={{
            albumsPaginationContext,
            renderArtistReleasesPagination,
          }}
        />
        <CommandPaletteContainer
          render={({
            doCopyText,
            renderCommand,
            promptUserSearch,
            goToRoute,
            t,
            CommandPalette,
          }) => (
            <CommandPalette
              commands={buildArtistPageCommandPalette({
                artistInfo,
                albumUri:
                  typeof fromRelease === 'string' ? fromRelease : undefined,
                trackUri: typeof fromTrack === 'string' ? fromTrack : undefined,
                doCopyText,
                goToRoute,
                promptUserSearch,
                t,
              })}
              renderCommand={renderCommand}
              onAfterOpen={() => {
                sendEvent({
                  eventCategory: EVENT_CATEGORY.COMMAND_PALETTE,
                  eventAction: EVENT_ACTION.OPEN,
                  eventLabel: 'Command Palette opened on artist page',
                  product: GA_PRODUCT_CATALOG,
                });
              }}
            />
          )}
        />
      </GridLayout>
    </Container>
  );
};

export default withAuth(ArtistPage);
